import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import BeneficiaryAccountViewAll from "../../../containers/Accounts/BeneficiaryAccount/BeneficiaryAccountViewAll";
import ReimbursementAccountViewAll from "../../../containers/Accounts/ReimbursementAccount/ReimbursementAccountViewAll/index.jsx";
import FieldForceAccountViewAll from "../../../containers/FieldForceAccount/FieldForceAccountViewAll";
import LargePopupRedirect from "../CommonPopups/CommonLargePopupRedirect";
import SmallPopup from "../CommonSmallPopup";
import { GlobalConfiguration } from "../../../utils/configurationProvider";
import ICONS from "../../../assets/Icons";
import { getAllBeneficiaryAccounts } from "../../../redux/features/accounts/beneficiaryAccount/index.js";
import { ReimburseViewAll } from "../../../redux/features/accounts/ReimburseAccount";
import { getAllFieldForceAccount } from "../../../redux/features/accounts/fieldForceAccount";

/**
 * Confirm Create Account Popup
 *
 * A reusable component that displays associated account information on the final step of account creation based on respective flow.
 * It renders a popup with account details and allows toggling between PAN and Bank details.
 *
 * @param {Object} props - Component props
 *
 * @param {string} [props.pan]
 * PAN number used for fetching associated accounts
 *
 * @param {string} [props.ifsc]
 * IFSC code of the bank account used for fetching associated accounts
 *
 * @param {string} [props.bank]
 * Bank account number used for fetching associated accounts
 *
 * @param {string} props.currentAssociate
 * Currently selected associate type from dropdown [It can be either "Beneficiary", "Reimbursement", "Field Force"]
 *
 * @param {(value: string) => void} props.setCurrentAssociate
 * Function to update selected associate type [It can be either "Beneficiary", "Reimbursement", "Field Force"]
 *
 * @param {boolean} props.openAssociatePopup
 * Controls visibility of the popup
 *
 * @param {(value: boolean) => void} props.setOpenAssociatePopup
 * Function to open/close the popup
 *
 * @param {number} props.dropDownNo
 * Index of the selected dropdown option for selecting associate type
 *
 * @param {string} props.activeType
 * View data on the basis of active tab type (Pan number or Bank account number)
 *
 * @param {string} props.setActiveType
 * Toggle data on the basis of Pan number or Bank account number
 *
 * * @param {string} props.handleClick
 * handleClick function which would carry out the functionality of creating a account
 *
 * @returns {JSX.Element}
 * Returns the UI for associated accounts and popup view
 */

const ALL_ASSOCIATE_TYPES = [
  "Beneficiary account",
  "Reimbursement account",
  "Field force account",
];

const ConfirmCreateAccountPopup = ({
  pan,
  ifsc,
  bank,
  activeType = "Pan",
  setActiveType,
  setShowModalPopup,
  currentAssociate,
  setCurrentAssociate,
  openAssociatePopup,
  setOpenAssociatePopup,
  activeAssociateId = "",
  dropDownNo = GlobalConfiguration.lengthCheckZero,
  handleClick,
  createFieldForceAccountLoading,
  allowedAssociateTypes = ALL_ASSOCIATE_TYPES,
  associateType,
}) => {
  const dispatch = useDispatch();

  const dropDownOptionLabels = allowedAssociateTypes;

  const [dataAvailability, setDataAvailability] = useState({
    "Beneficiary account": true,
    "Reimbursement account": true,
    "Field force account": true,
  });
  const [availabilityChecked, setAvailabilityChecked] = useState(false);

  const [isalertPopup, setIsalertPopup] = useState(false);
  const [flowSelectedId, setFlowSelectedId] = useState("");

  const [panNum, setPanNum] = useState(pan);
  const [ifscCode, setIfscCode] = useState("");
  const [bankAccNum, setBankAccNum] = useState("");

  const [defaultAssociate, setDefaultAssociate] = useState(
    dropDownOptionLabels[dropDownNo],
  );

  const navigate = useNavigate();

  useEffect(() => {
    if (allowedAssociateTypes.length <= GlobalConfiguration?.lengthCheckOne)
      return;

    if (!pan && !ifsc && !bank) return;

    let cancelled = false;

    const checkAvailability = async () => {
      const commonParams =
        activeType === "Bank"
          ? {
              ifsc,
              bank_account_no: bank,
              showToast: false,
            }
          : {
              pan,
              showToast: false,
            };

      const [beneRes, reimRes, ffRes] = await Promise.all([
        dispatch(getAllBeneficiaryAccounts(commonParams)),
        dispatch(ReimburseViewAll(commonParams)),
        dispatch(getAllFieldForceAccount(commonParams)),
      ]);

      if (cancelled) return;

      const availability = {
        "Beneficiary account":
          (beneRes?.payload?.total_count ||
            GlobalConfiguration?.lengthCheckZero) >
          GlobalConfiguration?.lengthCheckZero,
        "Reimbursement account":
          (reimRes?.payload?.total_count ||
            GlobalConfiguration?.lengthCheckZero) >
          GlobalConfiguration?.lengthCheckZero,
        "Field force account":
          (ffRes?.payload?.total_count ||
            GlobalConfiguration?.lengthCheckZero) >
          GlobalConfiguration?.lengthCheckZero,
      };

      setDataAvailability(availability);
      setAvailabilityChecked(true);

      const preferredDefault = dropDownOptionLabels[dropDownNo];
      const resolvedDefault = availability[preferredDefault]
        ? preferredDefault
        : dropDownOptionLabels.find((label) => availability[label]) ||
          preferredDefault;
      setDefaultAssociate(resolvedDefault);

      if (!availability[currentAssociate]) {
        const firstAvailable = dropDownOptionLabels.find(
          (label) => availability[label],
        );
        if (firstAvailable && firstAvailable !== currentAssociate) {
          setCurrentAssociate(firstAvailable);
          setActiveType("Pan");
          setPanNum(pan);
          setIfscCode("");
          setBankAccNum("");
        }
      }
    };

    checkAvailability();

    return () => {
      cancelled = true;
    };
  }, [pan, ifsc, bank, allowedAssociateTypes]);

  const dropDownOptions = availabilityChecked
    ? dropDownOptionLabels.map((label) => ({
        label,
        disabled: !dataAvailability[label],
      }))
    : dropDownOptionLabels;

  const handleDropdownChange = (val) => {
    setCurrentAssociate(val);
    setActiveType("Pan");
    setPanNum(pan);
    setIfscCode("");
    setBankAccNum("");
  };

  const handleCloseValue = () => {
    setCurrentAssociate(defaultAssociate);
  };

  const fullviewhandleFunction = () => {
    if (currentAssociate === "Field force account") {
      navigate(
        `/field_force_account_dashboard/field_force_account_view_all/field_force_account_detail_page/${flowSelectedId}`,
      );
    } else if (currentAssociate === "Reimbursement account") {
      navigate(
        `/reimbursement_account_dashboard/reimbursement_account_view_all/reimbursement_account_detail_page/${flowSelectedId}`,
      );
    } else {
      navigate(
        `/beneficiary_account_dashboard/beneficiary_account_view_all/beneficiary_account_detail_page/${flowSelectedId}`,
      );
    }
    setFlowSelectedId("");
  };

  return (
    <>
      {isalertPopup && (
        <div className="fixed z-z-index-max flex items-center justify-center top-left-base left-left-base w-width-full h-height-full bg-PopUp-BackDrop bg-opacity-dark-opacity">
          <SmallPopup
            headerTitle={"Confirm leaving this page?"}
            headerFontWeight="semibold"
            headerFontColor="emphasis"
            textSize="font-lg"
            subtext={"Leaving this page will exit from the current flow."}
            btnOneText="Stay"
            btnOneHandleFunction={() => setIsalertPopup(false)}
            btnOneEnabledBgColor="var(--primary-bg)"
            btnOneOutLineColor="var(--text-primary)"
            btnOneEnabledTextColor="var(--text-primary)"
            btnOneIsDisabled={false}
            btnOneHoverOption="white"
            btnTwoText="Leave"
            btnTwoHandleFunction={() => {
              setIsalertPopup(false);
              fullviewhandleFunction();
            }}
            btnTwoEnabledBgColor="var(--negative-act)"
            btnTwoEnabledTextColor="var(--text-primary)"
            btnTwoIsDisabled={false}
            btnTwoDisabledBgColor="var(--text-disabled)"
            btnTwoIsLoaderNeeded={true}
            btnTwoHoverOption="black"
          />
        </div>
      )}
      <LargePopupRedirect
        closeiconhandleFunction={() => {
          setShowModalPopup(false);
          setFlowSelectedId("");
          setCurrentAssociate(defaultAssociate);
          setActiveType("Pan");
          setPanNum(pan);
          setIfscCode("");
          setBankAccNum("");
        }}
        isDropDownDisabled={false}
        isDropDownRequired={true}
        dropDownValue={currentAssociate}
        handleDropdownChange={handleDropdownChange}
        dropDownOptions={dropDownOptions}
        dropDownNo={dropDownNo}
        handleCloseValue={handleCloseValue}
        ispanbankRequired={true}
        initialPanTab={activeType}
        togglepan={() => {
          setActiveType("Pan");
          setPanNum(pan);
          setIfscCode("");
          setBankAccNum("");
        }}
        toggleBank={() => {
          setActiveType("Bank");
          setPanNum("");
          setIfscCode(ifsc);
          setBankAccNum(bank);
        }}
        redirectIcon={ICONS?.ADD_BUTTON_WHITE}
        headerTitle={
          associateType?.length > GlobalConfiguration?.lengthCheckZero
            ? "Associated accounts"
            : "Confirm creating this account?"
        }
        headersubtext={
          associateType?.length > GlobalConfiguration?.lengthCheckZero
            ? activeType === "Pan"
              ? `The accounts listed below are linked to the selected ${associateType} account.`
              : `The following existing accounts are linked to the provided bank across different account classification.`
            : undefined
        }
        isbottomSectionneed={
          !(associateType?.length > GlobalConfiguration?.lengthCheckZero)
        }
        isLoading={createFieldForceAccountLoading}
        isLoaderNeeded={true}
        bottomHeaderTextNeed={true}
        bottomHeaderText="On creating a new account, it gets added to the list of active accounts as a 'pending' account for the provided PAN and bank."
        btnText="Create"
        confirmbtnrequired={true}
        btnType={"button"}
        enabledBgColor={"var(--positive-act)"}
        hoverOption={"dark"}
        outLineColor={""}
        enabledTextColor={"var(--text-primary)"}
        handleFunction={handleClick}
      >
        {currentAssociate === "Field force account" ? (
          <FieldForceAccountViewAll
            panNum={panNum}
            ifscCode={ifscCode}
            bankAccNumber={bankAccNum}
            setFlowSelectedId={setFlowSelectedId}
            setIsalertPopup={setIsalertPopup}
            id={activeAssociateId}
            type={"confirm_create_account"}
            activeType={activeType}
          />
        ) : currentAssociate === "Reimbursement account" ? (
          <ReimbursementAccountViewAll
            panNum={panNum}
            ifscCode={ifscCode}
            bankAccNumber={bankAccNum}
            setFlowSelectedId={setFlowSelectedId}
            setIsalertPopup={setIsalertPopup}
            id={activeAssociateId}
            type={"confirm_create_account"}
            activeType={activeType}
          />
        ) : (
          <BeneficiaryAccountViewAll
            panNum={panNum}
            ifscCode={ifscCode}
            bankAccNumber={bankAccNum}
            setFlowSelectedId={setFlowSelectedId}
            setIsalertPopup={setIsalertPopup}
            id={activeAssociateId}
            type={"confirm_create_account"}
            activeType={activeType}
          />
        )}
      </LargePopupRedirect>
    </>
  );
};

export default ConfirmCreateAccountPopup;
